import { DEFAULT_LOCALE, directionOf } from './locales.js';
import { en } from './messages/en.js';
import { ur } from './messages/ur.js';
const CATALOGUES = {
    en,
    ur,
};
/** Every key a locale has its own text for. Used by the tests, and by whatever audits coverage. */
export function translatedKeys(locale) {
    return Object.keys(CATALOGUES[locale]);
}
const isPlural = (message) => typeof message !== 'string';
/**
 * U+2066 LEFT-TO-RIGHT ISOLATE, U+2068 FIRST STRONG ISOLATE, U+2069 POP DIRECTIONAL ISOLATE.
 *
 * `DESIGN-TOKENS.md` `RTL-03`: "Mixed-direction strings, an Urdu sentence containing a Latin brand
 * name and a Western number, must be tested. This is where RTL implementations break."
 *
 * A number dropped into a right-to-left sentence takes its neighbours' direction at the edges: `-3`
 * at the end of an Urdu line renders as `3-`, and a dioptre or an invoice number read backwards is
 * a different number. §6 is explicit that numbers never mirror. So in a right-to-left locale every
 * interpolated number is wrapped in an LTR isolate, and every interpolated string (a customer's
 * name, a job label) in a first-strong isolate so a Latin name inside an Urdu sentence does not
 * pull the punctuation around it. Left-to-right text gets no marks: they are invisible, but they
 * would still break an exact-text assertion and add nothing.
 */
const LRI = String.fromCodePoint(0x2066);
const FSI = String.fromCodePoint(0x2068);
const PDI = String.fromCodePoint(0x2069);
/**
 * A number as digits. `String`, never `toLocaleString`: a locale can substitute Arabic-Indic
 * digits, and `D-03`/`TY-03` fix them as Western everywhere including the Urdu interface. Grouping
 * is the caller's business (`apps/counter/src/money.ts` owns it), because a count in a sentence is
 * small and money is not.
 */
const digits = (value) => String(value);
function fill(template, params, locale) {
    const rtl = directionOf(locale) === 'rtl';
    return template.replace(/\{(\w+)\}/g, (hole, name) => {
        const value = params[name];
        // A hole with no value stays visible. Throwing would take the till down over a typo in a
        // translation, and a blank would read as a sentence with a missing word.
        if (value === undefined)
            return hole;
        const text = typeof value === 'number' ? digits(value) : value;
        if (!rtl)
            return text;
        return typeof value === 'number' ? `${LRI}${text}${PDI}` : `${FSI}${text}${PDI}`;
    });
}
function pick(message, locale, params) {
    if (!isPlural(message))
        return message;
    const count = params['count'];
    if (typeof count !== 'number') {
        throw new TypeError('A plural message needs a numeric `count` parameter.');
    }
    return message[new Intl.PluralRules(locale).select(count) === 'one' ? 'one' : 'other'];
}
/**
 * The text for `key` in `locale`.
 *
 * **A missing key falls back to English, never to the key itself.** A key shown raw on a till
 * (`customerSearch.hint.none`) is not a sentence anybody can act on, and English is a language the
 * shop at least has a chance of reading. A key that exists in neither catalogue is a programming
 * error the types rule out; at runtime, reached by a cast, it returns the key rather than throwing.
 */
export function translate(locale, key, params = {}) {
    const message = CATALOGUES[locale][key] ?? CATALOGUES[DEFAULT_LOCALE][key];
    if (message === undefined)
        return key;
    return fill(pick(message, locale, params), params, locale);
}
//# sourceMappingURL=translate.js.map